import { topicPlanningSchema, type ArticleTopicPlanning } from "./articleTopicPlanning";

export type ArticleWorkbenchMatchedProduct = {
  id: string;
  title: string;
  description: string;
  url: string;
  imageUrl: string | null;
  availability: "in stock" | "out of stock" | "preorder";
  price: string;
  salePrice: string;
  brand: string;
  productType: string;
  googleProductCategory: string;
  itemGroupId: string;
  matchedTerms: string[];
  feedFetchedAt: string;
};

export type ArticleWorkbenchTopicBriefPack = {
  version: number;
  person: string;
  scene: string;
  product: string;
  brandInfo: string;
  purchaseRisk: string;
  decisionPath: string;
  audience: string;
  scenarios: string[];
  coreProblem: string;
  firstJudgment: string;
  productRole: string;
  problemAngles: string[];
  faqDirections: string[];
  doNotWrite: string[];
  entryPages: string[];
  sourceHint: string;
  matchedProducts: ArticleWorkbenchMatchedProduct[];
  productFeedFetchedAt: string | null;
  productMatchStatus: "matched" | "brand_fallback" | "feed_unavailable";
  planning?: ArticleTopicPlanning;
};

const BRIEF_PACK_VERSION = 1;

function toCleanText(value: unknown): string {
  if (typeof value !== "string") return "";
  return value.replace(/[\s\u00a0]+/g, " ").trim();
}

function toTextArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => toCleanText(item))
    .filter(Boolean)
    .slice(0, 6);
}

function uniqueOrder<T>(values: T[]): T[] {
  const result: T[] = [];
  for (const value of values) {
    if (!result.includes(value)) {
      result.push(value);
    }
  }
  return result;
}

function inferAudience(text: string): string {
  if (/拜拜|立香|供佛|祈福|祭祀/.test(text)) {
    return "正在做拜拜、供佛或祭祀用途判斷的人";
  }
  if (/臥香|線香|靜心|放鬆|薰香|居家/.test(text)) {
    return "想用臥香、線香做居家使用與放鬆情境的人";
  }
  if (/香環|24小時|4小時|長時間/.test(text)) {
    return "需要長時間供香，擔心間隔與安全擺放的人";
  }
  if (/香爐|香具|倒流香|香椎|香錐/.test(text)) {
    return "要搭配香具並確認使用方式的人";
  }
  if (/沉香|檀香|肖楠|降真香|惠安|老山|檜木/.test(text)) {
    return "在香材價值與香氣差異之間做選擇的人";
  }
  return "正在挑選香品與使用方式的人";
}

function inferPerson(text: string): string {
  if (/品牌推薦/.test(text)) {
    return "已經準備購買，但想先縮小品牌與商品選項的人";
  }
  if (/哪裡買/.test(text)) {
    return "已經要買線香或香品，但怕買到不適合家用商品的人";
  }
  if (/價格|買貴|價格落差/.test(text)) {
    return "第一次買香材或香品，怕買貴、怕規格看不懂的人";
  }
  if (/拜拜|供佛|祭祀|祖先/.test(text)) {
    return "家裡固定拜拜，怕香太刺鼻或買錯用途的人";
  }
  if (/臥香|線香/.test(text)) {
    return "想買臥香或線香做居家點香，但怕味道太重的人";
  }
  if (/香環|環香|盤香/.test(text)) {
    return "需要長時間供香，但擔心燃燒時間與擺放安全的人";
  }
  if (/倒流香|香椎|香錐/.test(text)) {
    return "想買倒流香做擺設或儀式感，但不確定香座與清潔成本的人";
  }
  return "";
}

function inferScenarios(text: string): string[] {
  const rows: string[] = [];
  if (/拜拜|供佛|祈福|祭祀|祖先/.test(text)) rows.push("拜拜、供佛與祭祀");
  if (/睡前|放鬆|靜心/.test(text)) rows.push("睡前放鬆");
  if (/臥香|線香|薰香|客廳|居家/.test(text)) rows.push("居家空間薰香");
  if (/24小時|4小時|長時間/.test(text)) rows.push("長時間持續供香");
  if (/香環|香爐|香具|倒流香|香椎|香錐/.test(text)) rows.push("香具與擺放方式");
  if (rows.length === 0) rows.push("日常使用情境");
  return uniqueOrder(rows);
}

function inferScene(text: string): string {
  if (/初一十五|祖先|拜拜|供佛|祭祀/.test(text)) {
    return "初一十五、供佛或祖先祭拜前的家用買香";
  }
  if (/睡前|放鬆|靜心/.test(text)) {
    return "睡前、靜心或短時間居家放鬆前的選香";
  }
  if (/客廳|薰香|居家|線香|臥香/.test(text)) {
    return "客廳或居家空間點香前的商品比較";
  }
  if (/24小時|12小時|4小時|香環|環香|盤香/.test(text)) {
    return "長時間供香前的燃燒時間與擺放判斷";
  }
  if (/品牌推薦|哪裡買|價格/.test(text)) {
    return "下單前比較價格、規格、用途和商品頁可信度";
  }
  return "";
}

function inferFirstJudgment(text: string): string {
  if (/拜拜|供佛|祭祀/.test(text)) {
    return "先判斷是否為祭祀/供奉場景，依場景決定燒香時間與煙感控制。";
  }
  if (/臥香|線香|靜心|放鬆/.test(text)) {
    return "先判斷空間用途與停留時長，再決定香型與使用方式。";
  }
  if (/香環|24小時|4小時|長時間/.test(text)) {
    return "先判斷是否可長時間進行與空間可視性，再選擇對應時間軸。";
  }
  if (/香爐|香具|倒流香|香椎|香錐/.test(text)) {
    return "先判斷要用何種擺放與搭配方式，避免空間不適。";
  }
  return "先判斷使用情境與敏感度，再回到香型、燃燒時間與空間通風做選擇。";
}

function inferPurchaseRisk(text: string): string {
  if (/價格|買貴|價格落差/.test(text)) {
    return "只看單一價格，沒有對照香材、規格、燃燒時間、用途與庫存，容易把不同用途的商品拿來硬比。";
  }
  if (/哪裡買|通路|店家/.test(text)) {
    return "只找哪裡買，沒有先確認用途、成分標示、規格與燃燒時間，容易買到不適合家用或當下情境的香品。";
  }
  if (/品牌推薦|排行榜|推薦/.test(text)) {
    return "只看品牌或排行榜，沒有確認品牌資訊是否能對應自己的使用空間、氣味接受度與商品規格。";
  }
  if (/香環|24小時|4小時|長時間/.test(text)) {
    return "只看燃燒時間，沒有先確認香具穩定、擺放位置、通風條件與是否需要長時間供香。";
  }
  if (/倒流香|香椎|香錐|香塔|香座/.test(text)) {
    return "只看倒流效果或造型，沒有確認香座是否適配、清潔成本與使用後的空間條件。";
  }
  if (/手串|手珠|佛珠/.test(text)) {
    return "只看材質名稱或圖片，沒有確認來源、規格、味道、保養方式與預算風險。";
  }
  return "";
}

function inferDecisionPath(text: string, firstJudgment: string): string {
  if (/價格|買貴|價格落差/.test(text)) {
    return "先分清楚用途與商品型態，再看香材、燃燒時間、規格與庫存，最後才判斷價格是否合理。";
  }
  if (/哪裡買|通路|店家/.test(text)) {
    return "先確認自己要用在拜拜、居家或送禮，再檢查商品頁是否清楚列出成分、規格、燃燒時間與購買資訊。";
  }
  if (/品牌推薦|排行榜|推薦/.test(text)) {
    return "先看品牌能不能提供可查證的成分與規格，再把商品放回自己的使用場景比較，不先照排行榜下單。";
  }
  if (/香環|24小時|4小時|長時間/.test(text)) {
    return "先確認是否真的需要長時間供香，再看香具、擺放位置、通風與燃燒時間，最後核對商品頁規格。";
  }
  if (/倒流香|香椎|香錐|香塔|香座/.test(text)) {
    return "先確認想要視覺效果還是日常點香，再看香座搭配、清潔方式、通風與商品規格。";
  }
  if (/手串|手珠|佛珠/.test(text)) {
    return "先確認收藏、配戴或送禮用途，再看材質來源、規格、保養方式與預算，最後才比較品項。";
  }
  return firstJudgment;
}

function inferProduct(text: string): string {
  if (/沉香臥香/.test(text)) return "沉香臥香";
  if (/檀香臥香/.test(text)) return "檀香臥香";
  if (/天然拜拜香|拜拜香|天然香|立香/.test(text)) return "天然立香、拜拜香";
  if (/線香/.test(text)) return "線香、臥香";
  if (/沉香粉/.test(text)) return "沉香粉";
  if (/沉香手串|檀香手串/.test(text)) return "沉香手串、檀香手串";
  if (/水沉香|奇楠沉香|沉香/.test(text)) return "沉香、沉香臥香";
  if (/香環|環香|盤香|12小時/.test(text)) return "香環、環香、盤香";
  if (/塔香/.test(text)) return "塔香";
  if (/倒流香/.test(text)) return "倒流香、香座";
  return "";
}

function inferProductRole(text: string): string {
  if (/沉香|檀香/.test(text)) {
    return "香材類負責主導香氣層次與用途價值感。";
  }
  if (/臥香|線香|無黏香/.test(text)) {
    return "線香與臥香分別承接居家、放鬆與短時使用情境。";
  }
  if (/香環|4小時|24小時/.test(text)) {
    return "香環類承接長時間供香與空間布局。";
  }
  if (/香爐|香具|倒流香|香椎|香錐/.test(text)) {
    return "香具類承接擺放方式與燃燒行為。";
  }
  if (/手串|手珠/.test(text)) {
    return "手串類承接收藏與日常品味使用。";
  }
  return "香品與香具在不同使用情境間分工承接，避免同題同質混淆。";
}

function inferProblemAngles(scenarios: string[]): string[] {
  const angleFromScenario = scenarios.map((scenario) => `判斷${scenario}是否該用的首要條件`);
  const base = [
    "先定義要用在什麼地方",
    "先比對煙感與空間敏感度",
    "先看成分標示與可公開規格",
  ];
  return uniqueOrder([...angleFromScenario, ...base]).slice(0, 5);
}

function parseEntryPages(nextAction: string, targetPage: string): string[] {
  const urls = nextAction.match(/https?:\/\/[^\s，,；;。)）]+/g) || [];
  const explicitPages = urls.length > 0 ? urls : targetPage ? [targetPage] : [];
  return uniqueOrder(explicitPages.map(toCleanText).filter(Boolean));
}

function inferCoreProblem(text: string, emphasis: string): string {
  if (/怕|擔心|困擾|卡在|無法/.test(text)) {
    return toCleanText(emphasis)
      .replace(/^SERP：?/i, "")
      .replace(/\s+/g, " ")
      .replace(/^[^。；;,]*?：/, "")
      .slice(0, 72);
  }
  if (/差別|比較|選購|選擇/.test(text)) {
    return `搜尋 ${text.split(/\s/)[0]} 後，常見卡關在用途切分與選擇順序。`;
  }
  return "";
}

export function buildArticleWorkbenchTopicBriefPack(input: {
  title: string;
  mainKeyword: string;
  targetPage?: string | null;
  emphasis?: string | null;
  nextAction?: string | null;
  matchedProducts?: ArticleWorkbenchMatchedProduct[];
  productFeedFetchedAt?: string | null;
  productMatchStatus?: ArticleWorkbenchTopicBriefPack["productMatchStatus"];
}): ArticleWorkbenchTopicBriefPack {
  const title = toCleanText(input.title);
  const keyword = toCleanText(input.mainKeyword);
  const targetPage = toCleanText(input.targetPage);
  const emphasis = toCleanText(input.emphasis);
  const nextAction = toCleanText(input.nextAction);

  const text = `${title} ${keyword} ${emphasis} ${nextAction}`;
  const subject = keyword || title;
  if (!title || !keyword) {
    throw new Error("建立題目問題包時缺少標題或主要關鍵字");
  }
  const audience = inferAudience(text) || `想解決「${subject}」選擇問題的讀者`;
  const person = inferPerson(text) || `正在判斷「${subject}」怎麼選的人`;
  const scenarios = inferScenarios(text);
  const scene = inferScene(text) || `準備了解或選購「${subject}」時`;
  const product = inferProduct(text) || subject;
  const brandInfo = "原木香官網、原木香品牌資訊與可公開商品規格";
  const firstJudgment = inferFirstJudgment(text);
  const purchaseRisk = inferPurchaseRisk(text) || `沒有先釐清「${subject}」的使用情境與商品差異。`;
  const decisionPath = inferDecisionPath(text, firstJudgment);
  const productRole = inferProductRole(text);
  const problemAngles = inferProblemAngles(scenarios);
  const coreProblem = inferCoreProblem(`${text}`, emphasis) || `想知道「${title}」該怎麼判斷。`;
  const entryPages = parseEntryPages(nextAction, targetPage);

  return {
    version: BRIEF_PACK_VERSION,
    person,
    scene,
    product,
    brandInfo,
    purchaseRisk,
    decisionPath,
    audience,
    scenarios,
    coreProblem,
    firstJudgment,
    productRole,
    problemAngles,
    faqDirections: problemAngles.slice(0, 3),
    doNotWrite: [],
    entryPages,
    sourceHint: `${keyword || title}`,
    matchedProducts: (input.matchedProducts || []).slice(0, 3),
    productFeedFetchedAt: toCleanText(input.productFeedFetchedAt) || null,
    productMatchStatus: input.productMatchStatus || (input.matchedProducts?.length ? "matched" : "brand_fallback"),
  };
}

export function normalizeArticleWorkbenchTopicBriefPack(raw: string | null | undefined): ArticleWorkbenchTopicBriefPack {
  if (!raw?.trim()) throw new Error("題目問題包不存在");
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) throw new Error("題目問題包格式錯誤");

    const person = toCleanText((parsed as any).person);
    const scene = toCleanText((parsed as any).scene);
    const product = toCleanText((parsed as any).product);
    const brandInfo = toCleanText((parsed as any).brandInfo);
    const purchaseRisk = toCleanText((parsed as any).purchaseRisk);
    const decisionPath = toCleanText((parsed as any).decisionPath);
    const audience = toCleanText((parsed as any).audience);
    const scenarios = toTextArray((parsed as any).scenarios);
    const coreProblem = toCleanText((parsed as any).coreProblem);
    const firstJudgment = toCleanText((parsed as any).firstJudgment);
    const productRole = toCleanText((parsed as any).productRole);
    const problemAngles = uniqueOrder(toTextArray((parsed as any).problemAngles).slice(0, 5));
    const faqDirections = uniqueOrder(toTextArray((parsed as any).faqDirections).slice(0, 5));
    const doNotWrite = uniqueOrder(toTextArray((parsed as any).doNotWrite).slice(0, 6));
    const entryPages = toTextArray((parsed as any).entryPages);
    const sourceHint = toCleanText((parsed as any).sourceHint);
    const version = Number((parsed as any).version);
    const productMatchStatus = (parsed as any).productMatchStatus;
    const requiredText = [person, scene, product, brandInfo, purchaseRisk, decisionPath, audience, coreProblem, firstJudgment, sourceHint];
    if (
      requiredText.some((value) => !value)
      || !Number.isInteger(version)
      || version < 1
      || scenarios.length < 1
      || problemAngles.length < 3
      || faqDirections.length < 3
      || !Array.isArray((parsed as any).matchedProducts)
      || !["matched", "brand_fallback", "feed_unavailable"].includes(productMatchStatus)
    ) {
      throw new Error("題目問題包缺少必要欄位");
    }

    return {
      version,
      person,
      scene,
      product,
      brandInfo,
      purchaseRisk,
      decisionPath,
      audience,
      scenarios,
      coreProblem,
      firstJudgment,
      productRole,
      problemAngles,
      faqDirections,
      doNotWrite,
      entryPages,
      sourceHint,
      matchedProducts: (parsed as any).matchedProducts.slice(0, 3),
      productFeedFetchedAt: toCleanText((parsed as any).productFeedFetchedAt) || null,
      productMatchStatus,
      ...((parsed as any).planning ? { planning: topicPlanningSchema.parse((parsed as any).planning) } : {}),
    };
  } catch (error) {
    if (error instanceof Error && /^題目問題包/.test(error.message)) throw error;
    throw new Error("題目問題包不是合法 JSON", { cause: error });
  }
}
